import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnInit,
  ViewChild, OnDestroy,
} from '@angular/core';
import { Location } from '@angular/common';
import {
  BehaviorSubject,
  ReplaySubject,
  Subscription,
  combineLatest,
  fromEvent,
  map,
  pairwise,
  shareReplay,
  startWith,
  takeUntil,
} from 'rxjs';
import { DrawerService } from 'src/app/services/ui/drawer.service';
import { CreationalPagesLayoutService } from 'src/app/services/ui/creational-pages-layout.service';
import { Router } from '@angular/router';
import { RoomsService } from 'src/app/services/rooms/rooms.service';
import { CommonProvider } from 'src/app/services/common/common.provider';
import { CookieService } from 'ngx-cookie-service';
import { AuthService } from 'src/app/auth.service';
import { UserService } from 'src/app/services/users/users.service';
import { WellnessNotificationService } from 'src/app/services/wellness/wellness-notification.service';

@Component({
  selector: 'app-creational-pages-layout',
  templateUrl: './creational-pages-layout.component.html',
  styleUrls: ['./creational-pages-layout.component.scss'],
})
export class CreationalPagesLayoutComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('headerContainer') headerContainer!: ElementRef<HTMLElement>;
  public headerTitle!: string;
  public headerTitleIcon!: 'icon_create' | 'icon_class';
  public headerButtonText!: string;
  public headerButtonIcon!: 'icon_create' | 'icon_class';
  public headerRefreshButtonText!: string;

  public headerBgConfig = new BehaviorSubject<{
    mainColor: string;
    secondaryColor: string;
    fontColor: string;
  }>({
    mainColor: '#ededed',
    secondaryColor: '#ededed',
    fontColor: '#000',
  });
  public disableBreadcrum = false;
  public footerConfig!: {
    submitButtonText: string;
    dismissButtonText?: string;
    deleteButtonText?: string;
  };

  public isTeacher = false;
  public canViewWellness = false;
  public wellnessEnabled = false;
  public hasBackButton = false;
  public isDrawerOpen = false;
  public windowResizeListener = fromEvent(window, 'resize').pipe(
    map((event: any) => event.target.innerWidth),
    startWith(window.innerWidth),
    shareReplay()
  );
  private drawerOpenSubscriptionRef!: Subscription;
  private drawerSubscriptionRef!: Subscription;
  public transitionInConfig: Partial<CSSStyleDeclaration> = {
    transitionProperty: 'width',
    transitionDuration: '255ms',
    transitionDelay: '290ms',
  };
  public transitionOutConfig: Partial<CSSStyleDeclaration> = {
    transitionProperty: 'width',
    transitionDuration: '205ms',
    transitionDelay: '0ms',
  };
  public destroyed$ = new ReplaySubject(1);

  constructor(
    private drawerService: DrawerService,
    private creationalPagesLayoutService: CreationalPagesLayoutService,
    public router: Router,
    private roomService: RoomsService,
    private commonProvider: CommonProvider,
    private cookieService: CookieService,
    private authService: AuthService,
    private userService: UserService,
    private cdr: ChangeDetectorRef,
    private wellnessNotificationService: WellnessNotificationService,
    private location: Location
  ) { }

  ngOnInit(): void {
    this.getUserData().subscribe();

    this.drawerSubscriptionRef = this.drawerService.isDrawerOpen.subscribe(
      (value) => (this.isDrawerOpen = value)
    );

    this.creationalPagesLayoutService.layoutConfig.subscribe((config: any) => {
      this.headerTitle = config.headerTitle;
      this.headerTitleIcon = config.headerTitleIcon;
      this.headerButtonText = config.headerButtonText;
      this.headerButtonIcon = config.headerButtonIcon;
      this.headerRefreshButtonText = config.headerRefreshButtonText;
      this.footerConfig = config.footerConfig;
      this.hasBackButton = config.showBackButton || false;
    });
  }

  ngAfterViewInit(): void {
    this.drawerOpenSubscriptionRef = combineLatest([
      this.drawerService.isDrawerOpen.pipe(startWith(true), pairwise()),
      this.windowResizeListener,
    ]).subscribe(([isDrawerOpen, windowInnerWidth]) => {
      const itWasOpened = isDrawerOpen.filter((value) => value).length !== 2;

      if (isDrawerOpen[1]) {
        this.drawerService.applyInAnimation(
          this.headerContainer,
          windowInnerWidth,
          itWasOpened ? this.transitionInConfig : {}
        );
      } else {
        this.drawerService.applyOutAnimation(
          this.headerContainer,
          windowInnerWidth,
          this.transitionOutConfig
        );
      }
    });
  }

  ngOnDestroy() {
    this.drawerSubscriptionRef.unsubscribe();
    this.drawerOpenSubscriptionRef.unsubscribe();
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  getUserData() {
    const token = this.cookieService.get('gr_access');
    const decode_token = this.authService.getDecodedAccessToken(token);

    return this.userService.getUserProfileData(decode_token.user_id).pipe(
      map((user) => {
        this.isTeacher = user.user_type === 'Teacher' || user.user_type === 'School';
        this.canViewWellness = user.user_type === 'Teacher' || user.user_type === 'School' || user.user_type === 'Principal';
        if (user.users_organizations.length) {
          const orgId = user.users_organizations[0].organization;
          this.getOrganization(orgId);
          this.creationalPagesLayoutService.institutionId.next(orgId);
        }

        if (this.canViewWellness && user.all_notifications) {
          this.wellnessNotificationService.setNotificationsFromApi(
            user.all_notifications,
            user.unread_notifications_count || 0
          );
        }
      }),
      takeUntil(this.destroyed$)
    );
  }

  public emitHeaderButton() {
    this.creationalPagesLayoutService.triggerClickHeaderButton = true;
  }

  public emitRefreshButton() {
    this.creationalPagesLayoutService.triggerClickRefreshButton = true;
  }

  public goBack(): void {
    this.location.back();
  }

  public getOrganization(institutionId: string) {
    this.roomService.getInstitutionHeader(institutionId).subscribe({
      next: (data) => {
        this.headerBgConfig.next({
          mainColor: data.primary_color,
          secondaryColor: data.secondary_color,
          fontColor: data.font_color,
        });
        this.wellnessEnabled = data.wellness_enabled;
        this.commonProvider.handleInstitutionChanges(data);
      },
    });
  }
}
